import { trace, SpanStatusCode } from '@opentelemetry/api';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { resourceFromAttributes } from '@opentelemetry/resources';
import type { Attributes, AttributeValue } from '@opentelemetry/api';
import { ATTR_SERVICE_NAME } from '@opentelemetry/semantic-conventions';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
} from '@opentelemetry/sdk-trace-base';

import { APP_NAME } from 'src/constants';

const APP_ENV = import.meta.env.VITE_APP_ENV ?? 'dev';
const DEPLOYMENT_ENV = APP_ENV === 'production' ? 'prod' : APP_ENV;
const OTEL_TRACES_URL = import.meta.env.VITE_OTEL_TRACES_URL?.trim() || '';
const MIN_PAGE_STAY_DURATION_MS = 1000;
const MAX_CLICK_TEXT_LENGTH = 80;
const MAX_CLICK_TEXT_PATH_DEPTH = 6;
const MAX_CLICK_CANDIDATE_TEXT_LENGTH = 160;
const CLICK_TEXT_UNSAFE_SELECTOR =
  'input,textarea,select,[contenteditable]:not([contenteditable="false"])';
const TELEMETRY_CONFIG = {
  enabled: Boolean(OTEL_TRACES_URL),
  sampleRate: 1,
  serviceName: `${APP_NAME}-${DEPLOYMENT_ENV}`,
  tracesUrl: OTEL_TRACES_URL,
} as const;

const tracer = trace.getTracer(`${APP_NAME}-tracer`);
let initialized = false;
let globalErrorHandlersRegistered = false;
let userBehaviorHandlersRegistered = false;
let pageLoadHandlerRegistered = false;
let pageLoadReported = false;
let tracerProvider: WebTracerProvider | undefined;
let currentPageStay: PageStayState | undefined;
type TelemetryAttributes = Record<string, AttributeValue | null | undefined>;
type PageLeaveReason = 'route_change' | 'hidden' | 'pagehide';
type ClickText = { source: string; text: string };

interface PageStayState {
  path: string;
  startedAt: number;
  reported: boolean;
}

export function initOpenTelemetry() {
  if (
    initialized ||
    !TELEMETRY_CONFIG.enabled ||
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return;
  }

  const resource = resourceFromAttributes({
    [ATTR_SERVICE_NAME]: TELEMETRY_CONFIG.serviceName,
    'deployment.environment': DEPLOYMENT_ENV,
  });

  const traceExporter = new OTLPTraceExporter({ url: TELEMETRY_CONFIG.tracesUrl });

  tracerProvider = new WebTracerProvider({
    resource,
    sampler: new ParentBasedSampler({
      root: new TraceIdRatioBasedSampler(TELEMETRY_CONFIG.sampleRate),
    }),
    spanProcessors: [new BatchSpanProcessor(traceExporter, { scheduledDelayMillis: 3000 })],
  });

  tracerProvider.register();

  registerPageLoadHandler();
  registerGlobalErrorHandlers();
  registerUserBehaviorHandlers();
  initialized = true;

  console.info(
    `[OTEL] initialized: service=${TELEMETRY_CONFIG.serviceName}, env=${DEPLOYMENT_ENV}`
  );
}

export function recordTelemetryError(error: unknown, attributes: TelemetryAttributes = {}) {
  if (
    !TELEMETRY_CONFIG.enabled ||
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return;
  }

  const normalizedError = normalizeError(error);
  const span = tracer.startSpan('frontend.error', {
    attributes: {
      'exception.message': normalizedError.message,
      'exception.type': normalizedError.name,
      'page.url': `${window.location.origin}${window.location.pathname}`,
      ...compactAttributes(attributes),
    },
  });

  span.recordException(normalizedError.error);
  span.setStatus({ code: SpanStatusCode.ERROR, message: normalizedError.message });
  span.end();
}

function registerGlobalErrorHandlers() {
  if (globalErrorHandlersRegistered) {
    return;
  }

  globalErrorHandlersRegistered = true;

  window.addEventListener('error', (event) => {
    recordTelemetryError(event.error ?? event.message, {
      'exception.source': 'window.error',
      'exception.filename': sanitizeUrl(event.filename),
      'exception.lineno': event.lineno,
      'exception.colno': event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    recordTelemetryError(event.reason, {
      'exception.source': 'window.unhandledrejection',
    });
  });
}

function registerPageLoadHandler() {
  if (pageLoadHandlerRegistered) {
    return;
  }

  pageLoadHandlerRegistered = true;

  if (document.readyState === 'complete') {
    setTimeout(recordPageLoad);
    return;
  }

  window.addEventListener('load', () => setTimeout(recordPageLoad), { once: true });
}

function recordPageLoad() {
  if (pageLoadReported) {
    return;
  }

  pageLoadReported = true;

  const metrics = getPageLoadMetrics();
  const span = tracer.startSpan('page.load', {
    attributes: compactAttributes({
      'page.url': `${window.location.origin}${window.location.pathname}`,
      'page.path': window.location.pathname,
      'page.load.duration_ms': metrics.duration,
      'page.load.dom_content_loaded_ms': metrics.domContentLoaded,
      'page.load.response_end_ms': metrics.responseEnd,
    }),
  });

  span.end();
}

function registerUserBehaviorHandlers() {
  if (userBehaviorHandlersRegistered) {
    return;
  }

  userBehaviorHandlersRegistered = true;
  currentPageStay = createPageStayState();
  document.addEventListener('click', handleClick, true);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', () => reportPageStay('pagehide'));
  wrapHistoryMethod('pushState');
  wrapHistoryMethod('replaceState');
  window.addEventListener('popstate', handleRouteChange);
}

function handleClick(event: MouseEvent) {
  const clickPathElements = getClickPathElements(event);
  const targetElement = clickPathElements[0];
  const clickText = getClickText(clickPathElements);

  const span = tracer.startSpan('user.click', {
    attributes: compactAttributes({
      'page.url': `${window.location.origin}${window.location.pathname}`,
      'event.target.tag': targetElement?.tagName.toLowerCase(),
      'event.target.role': targetElement?.getAttribute('role'),
      'event.target.type': targetElement?.getAttribute('type'),
      'event.target.aria_label': targetElement?.getAttribute('aria-label'),
      'event.target.title': targetElement?.getAttribute('title'),
      'event.target.text': clickText?.text,
      'event.target.text_source': clickText?.source,
    }),
  });

  span.end();
}

function handleVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    reportPageStay('hidden');
    flushTelemetry();
    return;
  }

  if (document.visibilityState === 'visible' && currentPageStay?.reported) {
    currentPageStay = createPageStayState();
  }
}

function handleRouteChange() {
  if (!currentPageStay) {
    currentPageStay = createPageStayState();
    return;
  }

  if (window.location.pathname === currentPageStay.path) {
    return;
  }

  reportPageStay('route_change');
  currentPageStay = createPageStayState();
}

function wrapHistoryMethod(method: 'pushState' | 'replaceState') {
  const originalMethod = window.history[method];

  window.history[method] = function wrappedHistoryMethod(...args) {
    const result = originalMethod.apply(this, args);
    handleRouteChange();
    return result;
  };
}

function reportPageStay(reason: PageLeaveReason) {
  if (!currentPageStay) {
    return;
  }

  if (currentPageStay.reported) {
    return;
  }

  const duration = Math.round(performance.now() - currentPageStay.startedAt);
  if (duration < MIN_PAGE_STAY_DURATION_MS) {
    return;
  }

  currentPageStay.reported = true;

  const span = tracer.startSpan('page.stay', {
    attributes: {
      'page.url': `${window.location.origin}${currentPageStay.path}`,
      'page.path': currentPageStay.path,
      'page.duration_ms': duration,
      'page.leave_reason': reason,
    },
  });

  span.end();

  if (reason === 'pagehide') {
    flushTelemetry();
  }
}

function createPageStayState(): PageStayState {
  return {
    path: window.location.pathname,
    reported: false,
    startedAt: performance.now(),
  };
}

function getClickPathElements(event: MouseEvent) {
  const pathElements = event
    .composedPath()
    .filter((target): target is Element => target instanceof Element);

  if (pathElements.length > 0) {
    return pathElements;
  }

  if (event.target instanceof Element) {
    return [event.target];
  }

  return event.target instanceof Node && event.target.parentElement
    ? [event.target.parentElement]
    : [];
}

function getClickText(pathElements: Element[]) {
  const targetElement = pathElements[0];
  if (!targetElement || targetElement.closest(CLICK_TEXT_UNSAFE_SELECTOR)) {
    return undefined;
  }

  const candidates = pathElements.slice(0, MAX_CLICK_TEXT_PATH_DEPTH);
  const telemetryLabelText = candidates
    .map((candidate) => getAttributeClickText(candidate, 'data-telemetry-label'))
    .find((text): text is ClickText => Boolean(text));
  if (telemetryLabelText) {
    return telemetryLabelText;
  }

  const accessibleText = candidates
    .map(
      (candidate) =>
        getLabelledByClickText(candidate) ||
        getAttributeClickText(candidate, 'aria-label') ||
        getAttributeClickText(candidate, 'title')
    )
    .find((text): text is ClickText => Boolean(text));
  if (accessibleText) {
    return accessibleText;
  }

  return candidates
    .map((candidate) =>
      candidate.matches(CLICK_TEXT_UNSAFE_SELECTOR)
        ? undefined
        : getSafeClickTextValue(candidate.textContent, 'text')
    )
    .find((text): text is ClickText => Boolean(text));
}

function getLabelledByClickText(element: Element) {
  const labelledBy = element.getAttribute('aria-labelledby');
  if (!labelledBy) {
    return undefined;
  }

  const text = labelledBy
    .split(/\s+/)
    .map((id) => document.getElementById(id)?.textContent)
    .filter(Boolean)
    .join(' ');

  return getSafeClickTextValue(text, 'aria-labelledby');
}

function getAttributeClickText(element: Element, attribute: string) {
  return getSafeClickTextValue(element.getAttribute(attribute), attribute);
}

function getSafeClickTextValue(text: string | null | undefined, source: string) {
  const normalizedText = text?.replace(/\s+/g, ' ').trim();
  if (!normalizedText || normalizedText.length > MAX_CLICK_CANDIDATE_TEXT_LENGTH) {
    return undefined;
  }

  return {
    source,
    text:
      normalizedText.length > MAX_CLICK_TEXT_LENGTH
        ? `${normalizedText.slice(0, MAX_CLICK_TEXT_LENGTH)}...`
        : normalizedText,
  };
}

function flushTelemetry() {
  tracerProvider?.forceFlush().catch((reason) => {
    console.error(reason);
  });
}

function getPageLoadMetrics() {
  const navigationEntry = performance.getEntriesByType('navigation')[0];

  if (navigationEntry instanceof PerformanceNavigationTiming) {
    return {
      domContentLoaded: Math.round(navigationEntry.domContentLoadedEventEnd),
      duration: Math.round(navigationEntry.loadEventEnd || navigationEntry.duration),
      responseEnd: Math.round(navigationEntry.responseEnd),
    };
  }

  const { domContentLoadedEventEnd, loadEventEnd, navigationStart, responseEnd } =
    performance.timing;

  return {
    domContentLoaded: Math.max(0, Math.round(domContentLoadedEventEnd - navigationStart)),
    duration: Math.max(0, Math.round(loadEventEnd - navigationStart)),
    responseEnd: Math.max(0, Math.round(responseEnd - navigationStart)),
  };
}

function sanitizeUrl(url?: string) {
  if (!url) {
    return undefined;
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);
    parsedUrl.search = '';
    parsedUrl.hash = '';
    return parsedUrl.toString();
  } catch {
    return url.split(/[?#]/)[0];
  }
}

function normalizeError(error: unknown) {
  if (error instanceof Error) {
    return {
      error,
      message: error.message,
      name: error.name,
    };
  }

  const message = getUnknownErrorMessage(error);

  return {
    error: message,
    message,
    name: 'Error',
  };
}

function compactAttributes(attributes: TelemetryAttributes): Attributes {
  return Object.entries(attributes).reduce<Attributes>((nextAttributes, [key, value]) => {
    if (value !== null && value !== undefined) {
      nextAttributes[key] = value;
    }
    return nextAttributes;
  }, {});
}

function getUnknownErrorMessage(error: unknown) {
  if (typeof error === 'string') {
    return error;
  }

  try {
    return JSON.stringify(error) || String(error);
  } catch {
    return String(error);
  }
}
